export interface FinalReviewCaptureState {
  sceneMutated: boolean;
  phase: string;
  finalVerificationPrepared: boolean;
}

/** Only the first final-review packet is forced to be a room overview. */
export function shouldForceFinalEnvironmentOverview(state: FinalReviewCaptureState): boolean {
  return state.sceneMutated
    && state.phase === "verification"
    && !state.finalVerificationPrepared;
}

/**
 * A capture is ready for model judgment only when it covers a pending target.
 * Environment captures additionally need a complete panorama/plan/facade
 * packet and complete accumulated target coverage.
 */
export function captureHasReviewableTargets(result: unknown): boolean {
  if (!result || typeof result !== "object") return false;
  const record = result as Record<string, unknown>;
  if (record.context !== "targets" && record.context !== "environment") return false;
  const visualReview = record.visualReview && typeof record.visualReview === "object"
    ? record.visualReview as Record<string, unknown>
    : null;
  const coveredIds = Array.isArray(visualReview?.coveredIds)
    ? visualReview.coveredIds.filter((id): id is string => typeof id === "string")
    : [];
  const pendingIds = Array.isArray(record.pendingIdsBeforeCapture)
    ? record.pendingIdsBeforeCapture.filter((id): id is string => typeof id === "string")
    : [];
  const coversPending = coveredIds.length > 0
    && (!pendingIds.length || coveredIds.some((id) => pendingIds.includes(id)));
  if (!coversPending) return false;
  if (record.context === "targets") return true;
  const overview = record.overview && typeof record.overview === "object"
    ? record.overview as Record<string, unknown>
    : null;
  return record.coverageComplete !== false
    && overview?.environment === true
    && overview.hasPerspective === true
    && overview.hasPlan === true
    && overview.hasFront === true;
}

/**
 * A review closes the visual verification when its coverage is complete. In
 * the final stage that is not enough: the turn also needs the professional
 * judgment of the whole, registered against the panorama of the epoch. A
 * directed recapture reviewed on its own completes the evidence of a target,
 * not the review of the ensemble — closing on it would skip the one verdict
 * that compares the changed elements with each other and with the room.
 */
export function reviewCoverageComplete(result: unknown, finalStage = false): boolean {
  if (!result || typeof result !== "object") return false;
  const record = result as Record<string, unknown>;
  if (record.coverageComplete !== true) return false;
  if (!finalStage) return true;
  return record.environmentStatus === "approved" || record.environmentApproved === true;
}

export function visualIssuesOnlyNeedCoverage(issues: ReadonlyArray<string>): boolean {
  return issues.length === 0 || issues.every((issue) => (
    /faltam? ângulos? legíveis|cobertura visual|captura verificável/i.test(issue)
  ));
}

/**
 * What the final stage forces next. A rejection whose issues ask for a
 * correction sends the turn back to execution: forcing the critic to look at
 * the same capture again only produces "review until approved" (three verdicts
 * on one image, 19/08 16:49). Only coverage problems keep capture/review forced.
 */
export function finalStageForcedTool(state: {
  sceneMutated: boolean;
  phase: string;
  finalVerificationPrepared: boolean;
  visualCaptureDone: boolean;
  visualReviewDone: boolean;
  visualVerificationIssues: ReadonlyArray<string>;
}): "scene.viewport.capture" | "scene.viewport.review" | null {
  if (!state.sceneMutated) return null;
  if (state.phase !== "verification" && !state.finalVerificationPrepared) return null;
  const correctionPending = state.visualVerificationIssues.length > 0 && !visualIssuesOnlyNeedCoverage(state.visualVerificationIssues);
  if (correctionPending) return null;
  if (!state.visualCaptureDone) return "scene.viewport.capture";
  if (!state.visualReviewDone) return "scene.viewport.review";
  return null;
}
